// hooks/playground/useLineItemVisibility.js
import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import visibilityService from '../../services/visibilityService';

const VISIBILITY_QUERY_KEY = ['lineItemVisibility'];
const STORAGE_KEY = 'pl_hidden_items_cache';

export const useLineItemVisibility = () => {
  const queryClient = useQueryClient();
  
  // Local state for editing mode
  const [isEditMode, setIsEditMode] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(new Set());
  const [showHiddenItems, setShowHiddenItems] = useState(false);

  // Fetch hidden items with localStorage cache
  const {
    data: hiddenItems = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: VISIBILITY_QUERY_KEY,
    queryFn: async () => {
      // Try localStorage first for instant load
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) {
        try {
          const parsedCache = JSON.parse(cached);
          // console.log('📦 Loaded hidden items from cache:', parsedCache);
          
          // Set cache immediately, then fetch fresh data in background
          queryClient.setQueryData(VISIBILITY_QUERY_KEY, parsedCache);
        } catch (e) {
          console.error('Failed to parse cached hidden items:', e);
        }
      }

      // Fetch fresh data from API
      const freshData = await visibilityService.getHiddenItems();
      
      // Update localStorage with fresh data
      localStorage.setItem(STORAGE_KEY, JSON.stringify(freshData));
      // console.log('✅ Fetched fresh hidden items from API:', freshData);
      
      return freshData;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30,   // 30 minutes
  });

  // Save mutation with optimistic updates
  const saveMutation = useMutation({
    mutationFn: (accountIds) => visibilityService.saveHiddenItems(accountIds),
    
    // OPTIMISTIC UPDATE - runs immediately before API call
    onMutate: async (newHiddenItems) => {
      // console.log('🚀 Optimistic update: Setting hidden items to:', newHiddenItems);
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: VISIBILITY_QUERY_KEY });
      
      // Snapshot the current value
      const previousHidden = queryClient.getQueryData(VISIBILITY_QUERY_KEY);
      
      // Optimistically update UI immediately
      queryClient.setQueryData(VISIBILITY_QUERY_KEY, newHiddenItems);
      
      // Update localStorage immediately for instant future loads
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newHiddenItems));
      
      // Return context with snapshot for potential rollback
      return { previousHidden };
    },
    
    // On success
    onSuccess: (data, variables) => {
      // console.log('✅ API save successful:', variables);
      setPendingChanges(new Set());
      setIsEditMode(false);
    },
    
    // If API fails, rollback
    onError: (err, newHidden, context) => {
      console.error('❌ API save failed, rolling back:', err);
      
      // Rollback to previous state
      queryClient.setQueryData(VISIBILITY_QUERY_KEY, context.previousHidden);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(context.previousHidden || []));
      
      // Show error to user (you can add toast notification here)
      alert('Failed to save visibility settings. Please try again.');
    },
    
    // Always refetch after success or error to ensure consistency
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: VISIBILITY_QUERY_KEY });
    }
  });

  // Initialize pending changes when entering edit mode
  useEffect(() => {
    if (isEditMode) {
      setPendingChanges(new Set(hiddenItems));
    }
  }, [isEditMode, hiddenItems]);

  // Toggle visibility of a single item
  const toggleItemVisibility = useCallback((accountId) => {
    if (!isEditMode) return;

    setPendingChanges(prev => {
      const newSet = new Set(prev);
      if (newSet.has(accountId)) {
        newSet.delete(accountId);
      } else {
        newSet.add(accountId);
      }
      return newSet;
    });
  }, [isEditMode]);

  // Check if item is hidden
  const isItemHidden = useCallback((accountId) => {
    if (isEditMode) {
      return pendingChanges.has(accountId);
    }
    return hiddenItems.includes(accountId);
  }, [isEditMode, pendingChanges, hiddenItems]);

  // Enter edit mode
  const startEditing = useCallback(() => {
    setIsEditMode(true);
  }, []);

  // Save changes - triggers optimistic update
  const saveChanges = useCallback(() => {
    const accountIdsArray = Array.from(pendingChanges);
    console.log('💾 Saving visibility changes:', accountIdsArray);
    saveMutation.mutate(accountIdsArray);
  }, [pendingChanges, saveMutation]);

  // Cancel editing
  const cancelEditing = useCallback(() => {
    setPendingChanges(new Set(hiddenItems));
    setIsEditMode(false);
  }, [hiddenItems]);

  // Show/hide all hidden items temporarily
  const toggleShowHidden = useCallback(() => {
    setShowHiddenItems(prev => !prev);
  }, []);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    if (!isEditMode) return false;
    
    const currentSet = new Set(hiddenItems);
    const pendingSet = new Set(pendingChanges);
    
    if (currentSet.size !== pendingSet.size) return true;
    
    for (const item of currentSet) {
      if (!pendingSet.has(item)) return true;
    }
    
    return false;
  }, [isEditMode, hiddenItems, pendingChanges]);

  // Restore a single item
  const restoreItem = useCallback((accountId) => {
    if (isEditMode) {
      setPendingChanges(prev => {
        const newSet = new Set(prev);
        newSet.delete(accountId);
        return newSet;
      });
    } else {
      // If not in edit mode, save immediately with optimistic update
      const updated = hiddenItems.filter(id => id !== accountId);
      saveMutation.mutate(updated);
    }
  }, [isEditMode, hiddenItems, saveMutation]);

  return {
    // State
    hiddenItems: isEditMode ? Array.from(pendingChanges) : hiddenItems,
    isLoading,
    isError,
    error,
    isEditMode,
    showHiddenItems,
    isSaving: saveMutation.isPending,
    
    // Computed
    hiddenCount: isEditMode ? pendingChanges.size : hiddenItems.length,
    hasUnsavedChanges: hasUnsavedChanges(),
    
    // Actions
    toggleItemVisibility,
    isItemHidden,
    startEditing,
    saveChanges,
    cancelEditing,
    toggleShowHidden,
    restoreItem,
  };
};